/**
 * garabat-referral-common.js
 * ---------------------------------------------------------------------
 * Lógica compartida (legacy + signed_v1) del cliente de referral del theme.
 *
 * Diseño testeable: TODO el comportamiento vive en `createClient(deps)`, una
 * factory con dependencias inyectadas (`fetch`, `uuid`, `now`, `localStorage`,
 * `sessionStorage`, cookies, `location`, `config`, `locks`). Sin accesos
 * directos a `window`/`document`: los aporta `garabat-referral.js`.
 *
 * Coordinación entre pestañas — TRES capas:
 *   1. GENERACIÓN compartida por campaña en localStorage
 *      (`garabat_gen:<slug>` = { generationId, referralCode, nonce, createdAt }).
 *      Es la AUTORIDAD de intención; cada intención relevante crea una nueva.
 *   2. EXCLUSIÓN MUTUA cross-tab de las mutaciones del carrito vía Web Locks
 *      (`navigator.locks`, inyectado como `deps.locks`). El lock se mantiene
 *      SOLO durante la sección crítica de mutación del carrito, NUNCA durante
 *      `validate`. Fallback documentado sin Web Locks: serialización
 *      intra-pestaña + reconciliación (NO ofrece exclusión atómica cross-tab).
 *   3. RECONCILIACIÓN posterior a cada POST de carrito: tras escribir/revocar,
 *      se relee la generación vigente; si cambió, se reconcilia el carrito con
 *      la intención vigente (re-sincronizar su atribución, o dejar limpio si es
 *      `disabled`/pendiente), acotada a un máximo de pasadas. El éxito significa
 *      "el carrito quedó consistente con la generación vigente", no solo "Shopify
 *      devolvió lo que envié".
 *
 * `opSeq` local es solo una optimización intra-pestaña. `issuedAt` se usa solo
 * para expiración. El token HMAC es OPACO: se reenvía como note attribute;
 * nunca se calcula, inspecciona ni registra. Sin secretos.
 * ---------------------------------------------------------------------
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.GarabatReferralCommon = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var MAX_SLUG = 120;
  var MAX_REF = 120;
  var MAX_LANDING = 500;

  var ATTR_PREFIX = 'garabat_attr:';
  var GEN_PREFIX = 'garabat_gen:';
  var CART_LOCK_NAME = 'garabat-cart-attribution';
  var RECONCILE_MAX = 3;

  var SIGNED_CART_KEYS = ['garabat_attribution_id', 'garabat_attribution_token', 'garabat_campaign_slug', 'garabat_landing_url'];

  var VALIDATE_TIMEOUT_MS = 5000;
  var VALIDATE_RETRY_BACKOFF_MS = 500;
  var CART_TIMEOUT_MS = 5000;
  var CART_BACKOFFS_MS = [0, 500, 1500];

  // --- Helpers puros (exportados para test) ---
  function normalizeSlug(s) { if (typeof s !== 'string') return ''; return s.trim().toLowerCase().slice(0, MAX_SLUG); }
  function isValidSlug(s) { return typeof s === 'string' && s.length > 0 && s.length <= MAX_SLUG && SLUG_RE.test(s); }
  function normalizeRef(r) { if (typeof r !== 'string') return ''; return r.trim().slice(0, MAX_REF); }
  function isValidRef(r) { return typeof r === 'string' && r.length > 0 && r.length <= MAX_REF; }
  function isUuid(v) { return typeof v === 'string' && UUID_RE.test(v); }
  function fallbackUuidFrom(getRandomValues) {
    var b = new Uint8Array(16);
    getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < 16; i++) hex.push((b[i] + 0x100).toString(16).slice(1));
    return hex[0] + hex[1] + hex[2] + hex[3] + '-' + hex[4] + hex[5] + '-' + hex[6] + hex[7] + '-' + hex[8] + hex[9] + '-' + hex[10] + hex[11] + hex[12] + hex[13] + hex[14] + hex[15];
  }
  function normalizeOrigin(url) {
    if (typeof url !== 'string' || !url.trim()) return null;
    try { var u = new URL(url.trim()); return (u.protocol + '//' + u.host).toLowerCase(); } catch (_e) { return null; }
  }
  function hostOf(url) {
    if (typeof url !== 'string' || !url.trim()) return null;
    try { return new URL(url.trim()).host.toLowerCase(); } catch (_e) { return null; }
  }
  // H2: bloquea el override forzado de signed_v1 en PRODUCCIÓN. Normaliza el
  // host con URL (insensible a mayúsculas, sin ruta ni slash) y cubre TODOS los
  // alias de producción del proyecto en Vercel (`...-garabat`,
  // `...-git-main-garabat`, deployments `...-<hash>-garabat`), SIN bloquear
  // genéricamente `*.vercel.app` (staging sigue permitido).
  function isProductionBackend(url) {
    var h = hostOf(url);
    if (!h) return false;
    var bare = h.split(':')[0];
    if (bare.slice(-1) === '.') bare = bare.slice(0, -1); // FQDN con punto final equivale al mismo host
    if (bare === 'referrals.garabat.es') return true;
    if (bare.slice(-11) === '.vercel.app' && bare.indexOf('garabat-referral-service-production') === 0) return true;
    return false;
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  // --- Página de campaña (landing pegada en Shopify > Páginas) ---
  // El HTML de la landing es SOLO contenido (el editor de Páginas puede quitar
  // <script>); la lógica del formulario vive aquí y en el orquestador.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function isValidEmail(v) { return typeof v === 'string' && v.length <= 254 && EMAIL_RE.test(v.trim()); }
  // Enlace personal: la ficha del libro con ?ref=CODIGO (ahí el theme aplica
  // la atribución y el descuento del invitado). Solo https.
  function buildShareLink(productUrl, code) {
    if (typeof productUrl !== 'string' || !isValidRef(normalizeRef(code))) return null;
    try {
      var u = new URL(productUrl.trim());
      if (u.protocol !== 'https:') return null;
      u.searchParams.set('ref', normalizeRef(code));
      return u.toString();
    } catch (_e) { return null; }
  }
  // Mensaje para compartir: `{enlace}` y `{codigo}` se sustituyen.
  function fillShareTemplate(tpl, values) {
    var t = typeof tpl === 'string' && tpl.trim() ? tpl : 'Te invito a descubrir este libro con un descuento: {enlace}';
    return t.split('{enlace}').join(values.link || '').split('{codigo}').join(values.code || '');
  }
  function serverModeOf(cfg) { return cfg && cfg.attributionMode === 'signed_v1' ? 'signed_v1' : 'legacy'; }

  function createClient(deps) {
    deps = deps || {};
    var cfg = deps.config || {};
    var backendBaseUrl = (cfg.backendBaseUrl || '').replace(/\/+$/, '');
    var cookieName = cfg.cookieName || 'garabat_ref';
    var cookieMaxAgeDays = cfg.cookieMaxAgeDays || 30;
    var forcedSignedAllowedHere = !isProductionBackend(cfg.backendBaseUrl); // H2: prod bloquea el override

    var fetchFn = deps.fetch || null;
    var now = deps.now || function () { return Date.now(); };
    var uuid = deps.uuid || function () { throw new Error('no_uuid_provider'); };
    var getCookie = deps.getCookie || function () { return null; };
    var setRawCookie = deps.setRawCookie || function () {};
    var location = deps.location || { href: '', search: '' };
    var locks = deps.locks && typeof deps.locks.request === 'function' ? deps.locks : null;

    var timing = deps.timing || {};
    var validateBackoffMs = timing.validateBackoffMs != null ? timing.validateBackoffMs : VALIDATE_RETRY_BACKOFF_MS;
    var validateTimeoutMs = timing.validateTimeoutMs != null ? timing.validateTimeoutMs : VALIDATE_TIMEOUT_MS;
    var cartBackoffsMs = timing.cartBackoffsMs != null ? timing.cartBackoffsMs : CART_BACKOFFS_MS;
    var cartTimeoutMs = timing.cartTimeoutMs != null ? timing.cartTimeoutMs : CART_TIMEOUT_MS;

    var state = { opSeq: 0, lockChain: null, lastMode: null, lastReferralCode: null, lastSlug: null, store: null };

    // --- Storage con degradación LS -> SS -> memoria ---
    function isUsable(s) { if (!s) return false; try { var k = '__gbt_probe__'; s.setItem(k, '1'); s.removeItem(k); return true; } catch (_e) { return false; } }
    function wrapWeb(s) {
      return {
        get: function (k) { try { return s.getItem(k); } catch (_e) { return null; } },
        set: function (k, v) { try { s.setItem(k, v); } catch (_e) {} },
        remove: function (k) { try { s.removeItem(k); } catch (_e) {} },
      };
    }
    function memoryStore() { var m = {}; return { get: function (k) { return Object.prototype.hasOwnProperty.call(m, k) ? m[k] : null; }, set: function (k, v) { m[k] = String(v); }, remove: function (k) { delete m[k]; } }; }
    function getStore() {
      if (state.store) return state.store;
      if (isUsable(deps.localStorage)) state.store = wrapWeb(deps.localStorage);
      else if (isUsable(deps.sessionStorage)) state.store = wrapWeb(deps.sessionStorage);
      else state.store = memoryStore();
      return state.store;
    }
    function readJson(key) { var raw = getStore().get(key); if (!raw) return null; try { return JSON.parse(raw); } catch (_e) { getStore().remove(key); return null; } }

    // --- Generación compartida (autoridad de intención) ---
    function readGeneration(slug) { var g = readJson(GEN_PREFIX + slug); return g && g.generationId ? g : null; }
    function writeGeneration(slug, gen) { getStore().set(GEN_PREFIX + slug, JSON.stringify(gen)); return gen; }
    function newGeneration(slug, referralCode, nonce) { return writeGeneration(slug, { generationId: uuid(), referralCode: referralCode, nonce: nonce, createdAt: now() }); }
    function isCurrentGen(slug, gen) { var cur = readGeneration(slug); return !!cur && !!gen && cur.generationId === gen.generationId; }
    function establishSignedGeneration(slug, urlRef) {
      var cur = readGeneration(slug);
      if (urlRef) {
        if (cur && cur.referralCode === urlRef && cur.nonce) return { gen: cur, changed: false };
        var changed = !!(cur && cur.referralCode && cur.referralCode !== urlRef);
        return { gen: newGeneration(slug, urlRef, uuid()), changed: changed };
      }
      return { gen: cur, changed: false };
    }
    function establishDisabledGeneration(slug) { return newGeneration(slug, null, null); }

    // --- Atribución almacenada (namespaced por campaña) ---
    function readStoredAttribution(slug) { var obj = readJson(ATTR_PREFIX + slug); if (obj && obj.attributionId && obj.token && obj.campaignSlug) return obj; return null; }
    function putStoredAttribution(slug, attr) { getStore().set(ATTR_PREFIX + slug, JSON.stringify(attr)); return attr; }
    function clearStoredAttribution(slug) { if (slug) getStore().remove(ATTR_PREFIX + slug); }

    // --- HTTP con timeout ---
    async function fetchJson(url, options, timeoutMs) {
      if (!fetchFn) return { ok: false, networkError: true };
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, timeoutMs) : null;
      try {
        var res = await fetchFn(url, Object.assign({}, options, controller ? { signal: controller.signal } : {}));
        if (timer) clearTimeout(timer);
        var json = null;
        try { json = await res.json(); } catch (_e) { json = null; }
        return { ok: res.ok, status: res.status, json: json };
      } catch (err) {
        if (timer) clearTimeout(timer);
        var aborted = err && (err.name === 'AbortError');
        return { ok: false, timedOut: !!aborted, networkError: !aborted };
      }
    }

    async function fetchCampaignConfig(slug) {
      if (!backendBaseUrl || !isValidSlug(slug)) return null;
      var r = await fetchJson(backendBaseUrl + '/api/referrals/campaigns/' + encodeURIComponent(slug), { method: 'GET', headers: { Accept: 'application/json' } }, validateTimeoutMs);
      return r.ok && r.json ? r.json : null;
    }

    async function validateAttribution(params) {
      var url = backendBaseUrl + '/api/referrals/attributions/validate';
      var body = { campaignSlug: params.campaignSlug, referralCode: params.referralCode, clientNonce: params.clientNonce };
      if (params.landingUrl) body.landingUrl = params.landingUrl;
      if (params.utm) body.utm = params.utm;
      var payload = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
      var attempt = 0;
      for (;;) {
        var r = await fetchJson(url, payload, validateTimeoutMs);
        var transient = r.networkError || r.timedOut || (r.status >= 500);
        if (transient) { attempt++; if (attempt < 2) { await sleep(validateBackoffMs); continue; } return { valid: false, reason: 'unavailable' }; }
        if (r.status === 429) return { valid: false, reason: 'rate_limited', deferred: true };
        if (r.status === 403 || r.status === 413 || r.status === 422) return { valid: false, reason: 'client_error' };
        if (r.status === 200 && r.json) return { valid: !!r.json.valid, data: r.json, reason: r.json.reason };
        return { valid: false, reason: 'unavailable' };
      }
    }

    // --- Carrito: primitivas de bajo nivel (sin lock) ---
    function signedAttributes(attr) {
      return { garabat_attribution_id: attr.attributionId, garabat_attribution_token: attr.token, garabat_campaign_slug: attr.campaignSlug, garabat_landing_url: location.href, garabat_ref: '' };
    }
    function revokeAttributes() { var a = {}; for (var i = 0; i < SIGNED_CART_KEYS.length; i++) a[SIGNED_CART_KEYS[i]] = ''; return a; }
    function cartHasSigned(cart, attr) {
      if (!cart || !cart.attributes) return false;
      var a = cart.attributes;
      return a.garabat_attribution_id === attr.attributionId && a.garabat_attribution_token === attr.token && a.garabat_campaign_slug === attr.campaignSlug;
    }
    function cartCleared(cart) { var a = (cart && cart.attributes) || {}; for (var i = 0; i < SIGNED_CART_KEYS.length; i++) { if (a[SIGNED_CART_KEYS[i]]) return false; } return true; }
    async function getCart() { var r = await fetchJson('/cart.js', { method: 'GET', headers: { Accept: 'application/json' } }, cartTimeoutMs); return r.ok && r.json ? r.json : null; }
    async function postCart(attributes) { return await fetchJson('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attributes }) }, cartTimeoutMs); }
    async function writeCartWithRetry(attributes, verifyFn, guardFn) {
      for (var i = 0; i < cartBackoffsMs.length; i++) {
        if (guardFn && !guardFn()) return { ok: false, reason: 'stale' };
        if (cartBackoffsMs[i] > 0) await sleep(cartBackoffsMs[i]);
        if (guardFn && !guardFn()) return { ok: false, reason: 'stale' };
        var r = await postCart(attributes);
        if (r.status === 422) return { ok: false, reason: 'unprocessable' };
        if (r.ok && r.json && verifyFn(r.json)) return { ok: true, cart: r.json };
      }
      return { ok: false, reason: 'unverified' };
    }

    // Reconciliación (SIN lock — se llama SIEMPRE ya dentro del lock). Acotada.
    // Deja el carrito consistente con la generación VIGENTE. Contrato de retorno
    // (H3): { reconciled, finalState: 'signed'|'cleared'|'unknown', reason? }.
    //  - Cada escritura se VERIFICA (writeCartWithRetry con verifyFn; NUNCA
    //    postCart crudo como prueba de éxito).
    //  - Orden de preferencia: (1) restaurar la atribución vigente confirmada;
    //    (2) carrito limpio confirmado; (3) NUNCA conservar deliberadamente una
    //    firma stale conocida; (4) si nada se confirma -> 'unknown'.
    //  - Aislamiento por campaña: la limpieza fail-safe solo toca firmas cuyo
    //    `garabat_campaign_slug === slug`.
    //  - No hay recursión: usa SOLO primitivas verificadas (writeCartWithRetry),
    //    nunca syncCart/revokeCart (que reconcilian).
    async function reconcileBounded(slug) {
      for (var i = 0; i < RECONCILE_MAX; i++) {
        var gen = readGeneration(slug);
        var stored = gen && gen.referralCode ? readStoredAttribution(slug) : null;
        var wantSync = !!(stored && stored.referralCode === gen.referralCode && stored.expiresAt && Date.parse(stored.expiresAt) > now());
        var cart = await getCart();
        if (wantSync) {
          if (cartHasSigned(cart, stored)) return { reconciled: true, finalState: 'signed' }; // ya consistente
          var target = stored;
          var w = await writeCartWithRetry(signedAttributes(target), function (c) { return cartHasSigned(c, target); }, null);
          if (w.ok) return { reconciled: true, finalState: 'signed' };        // restaurada y VERIFICADA
        } else {
          var a = (cart && cart.attributes) || {};
          if (cartCleared(cart)) return { reconciled: true, finalState: 'cleared' };
          if (!(a.garabat_campaign_slug === slug && a.garabat_attribution_id)) return { reconciled: true, finalState: 'cleared', reason: 'not_ours' }; // firma de otra campaña: no se toca
          var r = await writeCartWithRetry(revokeAttributes(), cartCleared, null);
          if (r.ok) return { reconciled: true, finalState: 'cleared' };       // limpiada y VERIFICADA
        }
      }
      // FAIL-SAFE: no se confirmó el estado deseado. Nunca conservar una firma
      // stale conocida: intentar LIMPIAR (condicionado por campaña) y verificar.
      var finalCart = await getCart();
      if (cartCleared(finalCart)) return { reconciled: true, finalState: 'cleared' };
      var fa = (finalCart && finalCart.attributes) || {};
      if (!(fa.garabat_campaign_slug === slug && fa.garabat_attribution_id)) {
        // La firma presente es de OTRA campaña: aislamiento -> no se limpia.
        return { reconciled: false, finalState: 'unknown', reason: 'not_ours' };
      }
      var rr = await writeCartWithRetry(revokeAttributes(), cartCleared, null);
      if (rr.ok) return { reconciled: true, finalState: 'cleared' };
      return { reconciled: false, finalState: 'unknown', reason: 'reconciliation_failed' };
    }

    // Sección crítica bajo Web Locks (o fallback intra-pestaña). NUNCA envuelve
    // `validate`, solo la mutación del carrito.
    function withCartLock(fn) {
      if (locks) return locks.request(CART_LOCK_NAME, function () { return fn(); });
      var prev = state.lockChain || Promise.resolve();
      var next = prev.then(function () { return fn(); }, function () { return fn(); });
      state.lockChain = next.then(function () {}, function () {});
      return next;
    }

    async function syncCartCore(attr, gen) {
      var slug = attr.campaignSlug;
      if (gen && !isCurrentGen(slug, gen)) { var rec0 = await reconcileBounded(slug); return { synced: false, stale: true, reconciliation: rec0 }; }
      var res = await writeCartWithRetry(signedAttributes(attr), function (c) { return cartHasSigned(c, attr); }, gen ? function () { return isCurrentGen(slug, gen); } : null);
      // Guard POSTERIOR: ¿perdimos la generación durante el POST en vuelo?
      if (gen && !isCurrentGen(slug, gen)) { var rec1 = await reconcileBounded(slug); return { synced: false, stale: true, reconciliation: rec1 }; }
      return { synced: res.ok, reason: res.reason };
    }
    function syncCart(attr, gen) { return withCartLock(function () { return syncCartCore(attr, gen); }); }

    async function revokeCartCore(slug, expectedAttributionId, gen) {
      if (gen && !isCurrentGen(slug, gen)) { var rec0 = await reconcileBounded(slug); return { revoked: false, stale: true, reconciliation: rec0 }; }
      var cart = await getCart();
      var a = (cart && cart.attributes) || {};
      var holdsCampaign = a.garabat_campaign_slug === slug;
      var holdsId = expectedAttributionId ? a.garabat_attribution_id === expectedAttributionId : !!a.garabat_attribution_id;
      if (!holdsCampaign || !holdsId) return { revoked: false, reason: 'not_ours' };
      var res = await writeCartWithRetry(revokeAttributes(), cartCleared, gen ? function () { return isCurrentGen(slug, gen); } : null);
      // Guard POSTERIOR a la revocación: si otra intención escribió algo válido
      // mientras tanto, reconciliar (no dejar el carrito contradiciendo la vigente).
      if (gen && !isCurrentGen(slug, gen)) { var rec1 = await reconcileBounded(slug); return { revoked: false, stale: true, reconciliation: rec1 }; }
      return { revoked: res.ok, reason: res.reason };
    }
    function revokeCart(slug, expectedAttributionId, gen) { return withCartLock(function () { return revokeCartCore(slug, expectedAttributionId, gen); }); }
    function revokeCartSafe(slug, expectedAttributionId, gen) {
      try { return revokeCart(slug, expectedAttributionId, gen).then(function (r) { return r; }, function () { return { revoked: false, reason: 'error' }; }); }
      catch (_e) { return Promise.resolve({ revoked: false, reason: 'error' }); }
    }
    function reconcileCart(slug) { return withCartLock(function () { return reconcileBounded(slug); }); }

    // --- Cookie / referralCode ---
    function getQueryParam(name) { try { return new URL(location.href).searchParams.get(name); } catch (_e) { return null; } }
    function setCookie(name, value, days) { var expires = new Date(now() + days * 24 * 60 * 60 * 1000).toUTCString(); setRawCookie(name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax'); }
    function readUrlRef() { var fromUrl = getQueryParam('ref') || getQueryParam('referralCode'); if (!fromUrl) return null; var ref = normalizeRef(fromUrl); return ref || null; }
    function readCookieRef() { var cookie = getCookie(cookieName); return cookie ? normalizeRef(cookie) : null; }
    function writeLegacyCookies(ref) { setCookie(cookieName, ref, cookieMaxAgeDays); if (cfg.campaignSlug) setCookie('garabat_campaign_slug', cfg.campaignSlug, cookieMaxAgeDays); }

    // --- Camino LEGACY ---
    async function tryAddCartAttributesLegacy(referralCode, campaignSlug) {
      if (!fetchFn) return false;
      var attributes = { garabat_ref: referralCode || '', garabat_campaign_slug: campaignSlug || '', garabat_landing_url: location.href };
      try { var res = await fetchFn('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attributes }) }); return !!res.ok; } catch (_e) { return false; }
    }
    function trackOnBackendLegacy(referralCode, campaignSlug) {
      if (!fetchFn || !backendBaseUrl || !campaignSlug || !referralCode) return;
      try {
        fetchFn(backendBaseUrl + '/api/referrals/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ campaignSlug: campaignSlug, referralCode: referralCode, landingUrl: location.href, utm: { source: getQueryParam('utm_source') || undefined, medium: getQueryParam('utm_medium') || undefined, campaign: getQueryParam('utm_campaign') || undefined } }) })['catch'](function () {});
      } catch (_e) {}
    }
    async function runLegacy(referralCode, campaignSlug) {
      if (!referralCode) return { status: 'legacy_no_ref' };
      var ok = await tryAddCartAttributesLegacy(referralCode, campaignSlug);
      if (!ok) trackOnBackendLegacy(referralCode, campaignSlug);
      return { status: ok ? 'legacy_cart' : 'legacy_tracked' };
    }

    // --- Camino SIGNED_V1 ---
    // Fail-closed sin Web Locks: limpieza best-effort de cualquier firma de ESTA
    // campaña (carrito + storage). No valida, no escribe `garabat_ref`, no cae a
    // legacy. Un fallo de limpieza nunca bloquea la compra.
    async function failClosedCleanup(slug) {
      clearStoredAttribution(slug);
      try {
        var cart = await getCart();
        var a = (cart && cart.attributes) || {};
        if (a.garabat_campaign_slug === slug && a.garabat_attribution_id) {
          await writeCartWithRetry(revokeAttributes(), cartCleared, null);
        }
      } catch (_e) {}
    }
    // Descuento del INVITADO en su primera compra (Fase 0): el backend devuelve
    // en /attributions/validate el código del participante que invita (solo el
    // libro de la campaña, máx. 5 usos, uno por cliente) y aquí se aplica al
    // carrito con `discount` de /cart/update.js. Ese parámetro REEMPLAZA los
    // códigos del carrito, así que se conservan los que el cliente ya tuviera.
    // Best-effort: nunca lanza ni bloquea la compra, y no altera el estado de
    // la atribución (la recompensa del referrer depende solo de la firma).
    async function applyInviteeDiscountCore(attr, gen) {
      var code = attr && attr.discountCode;
      if (!code || !/^[A-Za-z0-9-]{3,64}$/.test(code)) return { applied: false, reason: 'no_code' };
      if (gen && !isCurrentGen(attr.campaignSlug, gen)) return { applied: false, reason: 'stale' };
      var cart = await getCart();
      if (!cart) return { applied: false, reason: 'no_cart' };
      var existing = [];
      var list = cart.discount_codes || [];
      for (var i = 0; i < list.length; i++) { if (list[i] && list[i].code) existing.push(String(list[i].code)); }
      for (var j = 0; j < existing.length; j++) { if (existing[j].toUpperCase() === code.toUpperCase()) return { applied: true, reason: 'already' }; }
      existing.push(code);
      var r = await fetchJson('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: existing.join(',') }) }, cartTimeoutMs);
      return { applied: !!r.ok, reason: r.ok ? 'applied' : 'cart_error' };
    }
    async function applyInviteeDiscountSafe(attr, gen) {
      try { return await withCartLock(function () { return applyInviteeDiscountCore(attr, gen); }); } catch (_e) { return { applied: false, reason: 'error' }; }
    }
    function collectUtm() { var utm = { source: getQueryParam('utm_source') || undefined, medium: getQueryParam('utm_medium') || undefined, campaign: getQueryParam('utm_campaign') || undefined }; if (!utm.source && !utm.medium && !utm.campaign) return undefined; return utm; }
    async function validateAndSync(slug, referralCode, gen, expiredRetriesLeft) {
      if (expiredRetriesLeft == null) expiredRetriesLeft = 1; // H1: cota de reintentos ante attribution_expired
      var mySeq = ++state.opSeq;
      var landingUrl = location.href && location.href.length <= MAX_LANDING ? location.href : undefined;
      var res = await validateAttribution({ campaignSlug: slug, referralCode: referralCode, clientNonce: gen.nonce, landingUrl: landingUrl, utm: collectUtm() });
      if (mySeq !== state.opSeq) return { status: 'stale_discarded' };
      if (!isCurrentGen(slug, gen)) return { status: 'stale_discarded' };

      if (res.valid && res.data) {
        var attr = { attributionId: res.data.attributionId, token: res.data.token, campaignSlug: res.data.campaignSlug, referralCode: referralCode, issuedAt: res.data.issuedAt, expiresAt: res.data.expiresAt };
        if (typeof res.data.discountCode === 'string' && res.data.discountCode) attr.discountCode = res.data.discountCode;
        if (!isCurrentGen(slug, gen)) return { status: 'stale_discarded' };
        putStoredAttribution(slug, attr);
        var sync = await syncCart(attr, gen); // bajo lock + reconciliación posterior
        if (sync.stale) return { status: 'stale_discarded' };
        await applyInviteeDiscountSafe(attr, gen);
        return { status: 'signed_ok' };
      }

      if (res.reason === 'attribution_expired' && expiredRetriesLeft > 0) {
        if (!isCurrentGen(slug, gen)) return { status: 'stale_discarded' };
        clearStoredAttribution(slug);
        var gen2 = newGeneration(slug, referralCode, uuid());
        return await validateAndSync(slug, referralCode, gen2, expiredRetriesLeft - 1); // H1: sin más recursión al agotar
      }

      if (!isCurrentGen(slug, gen)) return { status: 'stale_discarded' };
      clearStoredAttribution(slug);
      await revokeCartSafe(slug, undefined, gen);
      return { status: 'signed_invalid', reason: res.reason };
    }
    async function runSigned(urlRef, slug) {
      // FAIL-CLOSED sin Web Locks: sin coordinación cross-tab real NO se ejecuta
      // el flujo firmado (evita que el checkout capture una firma stale durante
      // una ventana transitoria). Aplica también al override forzado de staging,
      // porque `run()` enruta TODO modo signed_v1 por aquí. No cae a legacy.
      if (!locks) {
        if (isValidSlug(slug)) await failClosedCleanup(slug);
        return { status: 'signed_unsupported_no_lock' };
      }
      if (!isValidSlug(slug)) return { status: 'signed_invalid', reason: 'bad_slug' };
      var est = establishSignedGeneration(slug, urlRef);
      var gen = est.gen;
      if (!gen || !gen.referralCode) return { status: 'signed_no_ref' };

      if (est.changed) {
        var oldAttr = readStoredAttribution(slug);
        if (isCurrentGen(slug, gen)) clearStoredAttribution(slug);
        var expectedId = oldAttr && oldAttr.referralCode !== gen.referralCode ? oldAttr.attributionId : undefined;
        await revokeCartSafe(slug, expectedId, gen);
      }

      var referralCode = gen.referralCode;
      var stored = readStoredAttribution(slug);
      if (stored && stored.referralCode === referralCode && stored.expiresAt && Date.parse(stored.expiresAt) > now()) {
        var sync = await syncCart(stored, gen);
        if (sync.stale) return { status: 'stale_discarded' };
        await applyInviteeDiscountSafe(stored, gen);
        return { status: 'signed_cached' };
      }
      return await validateAndSync(slug, referralCode, gen);
    }
    async function runDisabled(slug) {
      var gen = establishDisabledGeneration(slug);
      if (isCurrentGen(slug, gen)) clearStoredAttribution(slug);
      return await revokeCartSafe(slug, undefined, gen);
    }

    function resolveMode(settingMode, serverConfig) {
      if (settingMode === 'disabled') return 'disabled';
      if (settingMode === 'signed_v1') { if (cfg.allowForcedSignedV1 === true && forcedSignedAllowedHere) return 'signed_v1'; return serverModeOf(serverConfig); }
      return serverModeOf(serverConfig);
    }

    async function run() {
      var settingMode = cfg.attributionMode || 'auto';
      var urlRef = readUrlRef();
      var campaignSlug = normalizeSlug(cfg.campaignSlug || getCookie('garabat_campaign_slug') || '');
      var slug = isValidSlug(campaignSlug) ? campaignSlug : null;

      if (settingMode === 'disabled') {
        state.lastMode = 'disabled'; state.lastSlug = slug;
        if (slug) await runDisabled(slug);
        return { mode: 'disabled' };
      }

      var serverConfig = slug ? await fetchCampaignConfig(slug) : null;
      if (serverConfig && typeof deps.onCampaignConfig === 'function') { try { deps.onCampaignConfig(serverConfig); } catch (_e) {} }

      var mode = resolveMode(settingMode, serverConfig);
      state.lastMode = mode; state.lastSlug = slug;

      if (mode === 'signed_v1') { state.lastReferralCode = urlRef; return { mode: mode, result: slug ? await runSigned(urlRef, slug) : { status: 'signed_invalid', reason: 'bad_slug' } }; }

      var legacyRef = urlRef || readCookieRef();
      if (urlRef) writeLegacyCookies(urlRef);
      state.lastReferralCode = legacyRef;
      return { mode: 'legacy', result: await runLegacy(legacyRef, slug) };
    }

    function reassertCart() {
      if (state.lastMode === 'signed_v1' && isValidSlug(state.lastSlug)) {
        var slug = state.lastSlug;
        // H6: sin Web Locks el flujo firmado NO opera (fail-closed). La
        // re-aserción tampoco firma: NO sincroniza, NO cae a legacy, NO /track;
        // hace cleanup best-effort (storage + firma de esta campaña) y devuelve
        // un estado explícito no exitoso. Un fallo de cleanup no bloquea.
        if (!locks) {
          return failClosedCleanup(slug).then(
            function () { return { status: 'signed_unsupported_no_lock' }; },
            function () { return { status: 'signed_unsupported_no_lock' }; },
          );
        }
        var gen = readGeneration(slug);
        var stored = readStoredAttribution(slug);
        if (gen && stored && stored.referralCode === gen.referralCode && stored.expiresAt && Date.parse(stored.expiresAt) > now()) return syncCart(stored, gen);
        return Promise.resolve({ synced: false, reason: 'no_signature' });
      }
      if (state.lastMode === 'legacy' && state.lastReferralCode) return tryAddCartAttributesLegacy(state.lastReferralCode, state.lastSlug);
      return Promise.resolve(null);
    }

    // Registro desde la página de campaña. Si este visitante llegó invitado por
    // alguien (ref en la URL o generación firmada/cookie legacy vigente), se
    // envía ese código: el backend lo fija como su referrer (primer registro
    // gana), y así cuenta como participante SECUNDARIO. Nunca lanza.
    async function join(input) {
      input = input || {};
      var slug = normalizeSlug(input.campaignSlug || cfg.campaignSlug || '');
      var email = typeof input.email === 'string' ? input.email.trim() : '';
      var firstName = typeof input.firstName === 'string' ? input.firstName.trim().slice(0, 80) : '';
      if (!isValidSlug(slug)) return { ok: false, reason: 'bad_campaign' };
      if (!firstName || !isValidEmail(email)) return { ok: false, reason: 'invalid_input' };
      if (!backendBaseUrl) return { ok: false, reason: 'not_configured' };
      var gen = readGeneration(slug);
      var invitedBy = readUrlRef() || (gen && gen.referralCode) || readCookieRef() || undefined;
      var body = { campaignSlug: slug, email: email, firstName: firstName, marketingConsent: input.marketingConsent === true, source: 'landing_page' };
      if (invitedBy) body.referralCode = invitedBy;
      var r = await fetchJson(backendBaseUrl + '/api/referrals/participants', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, validateTimeoutMs);
      if (r.status === 429) return { ok: false, reason: 'rate_limited' };
      if (r.status === 404) return { ok: false, reason: 'campaign_unavailable' };
      if (!r.ok || !r.json || !r.json.participant) return { ok: false, reason: 'unavailable' };
      var p = r.json.participant;
      if (!p.referralCode) return { ok: false, reason: 'code_pending' };
      return {
        ok: true,
        referralCode: p.referralCode,
        discountCode: p.shareDiscountCode || null,
        link: buildShareLink(input.productUrl, p.referralCode),
      };
    }

    return {
      run: run,
      join: join,
      reassertCart: reassertCart,
      readGeneration: readGeneration,
      establishSignedGeneration: establishSignedGeneration,
      establishDisabledGeneration: establishDisabledGeneration,
      isCurrentGen: isCurrentGen,
      readStoredAttribution: readStoredAttribution,
      putStoredAttribution: putStoredAttribution,
      clearStoredAttribution: clearStoredAttribution,
      resolveMode: resolveMode,
      fetchCampaignConfig: fetchCampaignConfig,
      validateAttribution: validateAttribution,
      syncCart: syncCart,
      revokeCart: revokeCart,
      reconcileCart: reconcileCart,
      getCart: getCart,
      runLegacy: runLegacy,
      runSigned: runSigned,
      runDisabled: runDisabled,
      _state: state,
    };
  }

  return {
    createClient: createClient,
    normalizeSlug: normalizeSlug,
    isValidSlug: isValidSlug,
    normalizeRef: normalizeRef,
    isValidRef: isValidRef,
    isUuid: isUuid,
    fallbackUuidFrom: fallbackUuidFrom,
    normalizeOrigin: normalizeOrigin,
    serverModeOf: serverModeOf,
    isValidEmail: isValidEmail,
    buildShareLink: buildShareLink,
    fillShareTemplate: fillShareTemplate,
  };
});
